<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  beneficiary: { type: Object, default: null },
  installment: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();
const address = computed(() => props.installment?.parcela?.endereco || {});
const cityLine = computed(() =>
  [address.value.bairro, address.value.cidade].filter(Boolean).join(' - ')
);
const streetLine = computed(() => {
  const street = [address.value.logradouro, address.value.numero]
    .filter(Boolean)
    .join(', ');
  const details = [address.value.unidade, address.value.complemento]
    .filter(Boolean)
    .join(' - ');
  return [street, details].filter(Boolean).join(' - ');
});
const beneficiaryName = computed(
  () =>
    props.beneficiary?.empresa?.name ||
    props.beneficiary?.nome ||
    props.beneficiary?.usuario?.name ||
    t('REAL_ESTATE_FINANCE.TABLE.ADDRESS_UNAVAILABLE')
);
const amount = computed(
  () =>
    props.beneficiary?.valor_total?.amount ||
    props.beneficiary?.valor_confirmado?.amount ||
    t('REAL_ESTATE_FINANCE.COMMON.EMPTY_MONEY')
);
const numericAmount = computed(() => amount.value.replace(/^R\$\s*/, ''));
</script>

<template>
  <LaisModal
    :show="show"
    max-width-class="max-w-[806px]"
    header-class="flex shrink-0 justify-end px-4 pt-4 sm:px-8 sm:pt-8"
    content-class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:min-h-[500px] sm:px-8 sm:py-6"
    @close="emit('close')"
  >
    <template #header>
      <span aria-hidden="true" />
    </template>
    <section data-testid="finance-release-dialog" class="flex flex-col gap-8">
      <h3 class="font-redHatDisplay text-lg font-semibold text-lais-text">
        {{ t('REAL_ESTATE_FINANCE.RELEASE.TITLE') }}
      </h3>
      <div class="flex flex-col gap-1 text-sm text-lais-text-body">
        <p class="flex items-center gap-2">
          <span
            class="i-lucide-map-pin size-4 text-lais-primary"
            aria-hidden="true"
          />
          <span>{{ cityLine }}</span>
        </p>
        <p class="pl-6">{{ streetLine }}</p>
      </div>
      <div class="text-sm text-lais-text-body">
        <p class="text-[12px] text-lais-text-muted">
          {{ t('REAL_ESTATE_FINANCE.RELEASE.BENEFICIARY') }}
        </p>
        <p class="mt-1 font-semibold">{{ beneficiaryName }}</p>
      </div>
      <div
        class="w-full rounded-lais border border-lais-border bg-white p-6 shadow-lais-card sm:max-w-[414px]"
      >
        <p class="text-sm text-lais-text-muted">
          {{ t('REAL_ESTATE_FINANCE.RELEASE.AMOUNT') }}
        </p>
        <p class="mt-4 flex items-baseline font-semibold text-lais-success">
          <span class="mr-1 text-sm">{{
            t('REAL_ESTATE_FINANCE.COMMON.CURRENCY')
          }}</span>
          <span class="text-[32px]">{{ numericAmount }}</span>
        </p>
      </div>
    </section>
    <template #footer>
      <div class="flex flex-wrap justify-end gap-3">
        <LaisPrimaryButton variant="ghost" size="lg" @click="emit('close')">
          {{ t('REAL_ESTATE_FINANCE.RELEASE.CANCEL') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          size="lg"
          :is-loading="loading"
          @click="emit('confirm')"
        >
          {{ t('REAL_ESTATE_FINANCE.RELEASE.CONFIRM') }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
